import { defineStore } from "pinia";
import { ref } from "vue";
import { Campaign, CampaignCreation, CampaignV4, ModuleLabel } from "@/types/campaign";
import { WeightingModule } from "@/types/campaign";

export interface StyleType {
  id: string;
  enabled: boolean;
}

export interface SkillInfo {
  id: string,
  id_v2?: string,
  id_v1?: string,
  type: "skill" | "style",
  duration: number,
  disabled: boolean,
  incompatible_versions: string[],
  isAvailaibleInEssential: boolean,
  style_types?: StyleType[],
}

export enum VersionComparison {
  INFERIOR = -1,
  EQUAL = 0,
  SUPERIOR = 1,
}

export const useSkillsStore = defineStore("skills", () => {
  const skills: Ref<SkillInfo[]> = ref<SkillInfo[]>([
    {
      id: "problem_solving",
      id_v1: "MS_RDPB",
      id_v2: "pb_solving",
      type: "skill",
      duration: 171, //in seconds
      incompatible_versions: [] as string[],
      isAvailaibleInEssential: false,
      disabled: false,
    },
    {
      id: "stress_management",
      id_v1: "MS_GDSS",
      id_v2: "stress_manage",
      type: "skill",
      duration: 251,
      incompatible_versions: [] as string[],
      isAvailaibleInEssential: false,
      disabled: false,
    },
    {
      id: "adaptability",
      id_v1: "MS_ADPT",
      type: "skill",
      duration: 123,
      incompatible_versions: [] as string[],
      isAvailaibleInEssential: false,
      disabled: false,
    },
    {
      id: "active_listening",
      id_v2: "active_listen",
      type: "skill",
      duration: 159,
      incompatible_versions: ["1.0.0"],
      isAvailaibleInEssential: true,
      disabled: false,
    },
    {
      id: "self_efficacy",
      id_v2: "auto_efficient",
      type: "skill",
      disabled: false,
      duration: 159,
      incompatible_versions: ["1.0.0", "3.0.0"],
      isAvailaibleInEssential: true,
    },
    {
      id: "decision_making",
      id_v2: "decisions",
      type: "skill",
      duration: 143,
      incompatible_versions: ["1.0.0"],
      isAvailaibleInEssential: true,
      disabled: false,
    },
    {
      id: "empathy",
      type: "skill",
      duration: 159,
      incompatible_versions: ["1.0.0"],
      isAvailaibleInEssential: true,
      disabled: false,
    },
    {
      id: "planning",
      type: "skill",
      disabled: false,
      duration: 436,
      incompatible_versions: ["1.0.0", "2.0.0"],
      isAvailaibleInEssential: false,
    },
    {
      id: "time_management",
      id_v2: "time_manage",
      type: "skill",
      duration: 251,
      incompatible_versions: ["1.0.0"],
      isAvailaibleInEssential: false,
      disabled: false,
    },
    {
      id: "communication",
      type: "style",
      disabled: true,
      duration: 4,
      incompatible_versions: [] as string[],
      isAvailaibleInEssential: false,
    },
    {
      id: "leadership",
      type: "style",
      disabled: false,
      duration: 146,
      incompatible_versions: ["1.0.0", "2.0.0"],
      isAvailaibleInEssential: true,
      style_types: [
        { id: "transformational", enabled: true },
        { id: "transactional", enabled: true },
        { id: "laissez_faire", enabled: true },
      ] as StyleType[],
    },
    {
      id: "team_work",
      type: "style",
      disabled: true,
      duration: 4,
      incompatible_versions: [] as string[],
      isAvailaibleInEssential: false,
    },
    {
      id: "learning_strategy",
      id_v2: "learn_strategy",
      type: "style",
      duration: 28,
      incompatible_versions: ["1.0.0"],
      isAvailaibleInEssential: true,
      disabled: false,
      style_types: [
        { id: "rehearsal", enabled: true },
        { id: "elaboration", enabled: true },
        { id: "organization", enabled: true },
        { id: "critical_thinking", enabled: true },
      ] as StyleType[],
    },
    {
      id: "motivation",
      type: "style",
      disabled: false,
      duration: 20,
      incompatible_versions: ["1.0.0"],
      isAvailaibleInEssential: true,
      style_types: [
        { id: "intrinsic", enabled: true },
        { id: "extrinsic", enabled: true },
        { id: "task_value", enabled: true },
        { id: "self_efficacy", enabled: true },
      ] as StyleType[],
    }
  ]);

  const scoredSkills = computed(() => {
    return skills.value.filter((s) => s.type === "skill");
  });

  const styleSkills = computed(() => {
    return skills.value.filter((s) => s.type === "style");
  });

  function getAdventureDuration(modules: ModuleLabel[]) {
    let seconds = 1140; // 20 minutes
    skills.value.filter(s => !['time_management', 'stress_management', 'empathy', 'active_listening', 'self_efficacy'].includes(s.id)).forEach((s) => {
      if (modules?.includes(s.id as WeightingModule)) {
        seconds = seconds + s.duration;
      }
    });
    if (modules?.includes('time_management') || modules?.includes('stress_management')) seconds = seconds + skills.value.find((s) => s.id === 'stress_management')?.duration;
    if (modules?.includes('empathy') || modules?.includes('active_listening') || modules?.includes('self_efficacy')) seconds = seconds + skills.value.find((s) => s.id === 'empathy').duration;
    return Math.round(seconds / 60);
  }

  function getFormattedSkillId(skillId: string) {
    const skill = skills.value.find(s => s.id === skillId || s.id_v2 === skillId || s.id_v1 === skillId || (s.id_v2 + "_score") === skillId);
    return skill?.id || skillId;
  }

  function getV1SkillId(skillId: string) {
    const skill = skills.value.find(s => s.id === skillId || s.id_v2 === skillId || s.id_v1 === skillId);
    return (skill?.id_v1 || skillId) as string;
  }

  function getV2SkillId(skillId: string) {
    const skill = skills.value.find(s => s.id === skillId || s.id_v2 === skillId || s.id_v1 === skillId);
    return (skill?.id_v2 || skill?.id || skillId) as string;
  }

  function isSkillCompatible(version: string, skillId) {
    const skill = skills.value.find(s => s.id === skillId || s.id_v2 === skillId || s.id_v1 === skillId);
    return !skill?.incompatible_versions?.includes(version);
  }

  function getCampaignStyles(campaign: Campaign): SkillInfo[] {
    return skills.value.filter((m) => {
      if (campaign?.assessment?.modules) {
        if (!campaign.assessment.modules.includes(m.id as ModuleLabel)) return false;
      }
      if (m.type !== "style") return false;
      if (campaign.variant === 'essentials' && m.isAvailaibleInEssential === false) return false;
      return !m.disabled && !m.incompatible_versions.includes(campaign.campaign_version);
    });
  }

  function getCampaignEvaluatedSkills(campaign: Campaign): SkillInfo[] {
    return skills.value.filter((m) => {
      if (campaign?.assessment?.modules) {
        if (!campaign.assessment.modules.includes(m.id as ModuleLabel)) return false;
      }
      if (m.type !== 'skill') return false;
      if (campaign.variant === 'essentials' && m.isAvailaibleInEssential === false) return false;
      return !m.disabled && !m.incompatible_versions.includes(campaign.campaign_version);
    });
  }

  function campaignMajorVersion(campaign: Campaign | CampaignCreation | string): number {
    if (typeof campaign === 'string') {
      return parseInt(campaign.split('.')[0]);
    }

    if (!campaign?.campaign_version) return 0;
    return parseInt(campaign.campaign_version.split('.')[0]);
  }

  function isCampaignV1(campaign: Campaign | CampaignCreation | string): boolean {
    return campaignMajorVersion(campaign) === 1;
  }

  function isCampaignV2(campaign: Campaign | CampaignCreation | string): boolean {
    return campaignMajorVersion(campaign) === 2;
  }

  function isCampaignV3OrMore(campaign: Campaign | CampaignCreation | string): boolean {
    return campaignMajorVersion(campaign) >= 3;
  }

  function isCampaignV4OrMore(campaign: Campaign | CampaignCreation | CampaignV4): campaign is CampaignV4 {
    return campaignMajorVersion(campaign as CampaignCreation) >= 4;
  }

  return {
    skills,
    scoredSkills,
    styleSkills,
    getFormattedSkillId,
    getV1SkillId,
    getV2SkillId,
    getAdventureDuration,
    isSkillCompatible,
    getCampaignStyles,
    getCampaignEvaluatedSkills,
    campaignMajorVersion,
    isCampaignV1,
    isCampaignV2,
    isCampaignV3OrMore,
    isCampaignV4OrMore
  };
});
